"use client"

import { useTranslations } from "next-intl"
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion"
import { useProjects } from "@/app/[locale]/projects/_api/use-project-queries"
import { useShopsWithAssociations } from "@/app/admin/shops/_api/use-shop-queries"
import { formatNumber } from "@/lib/utils/currency"
import { AnimatedSection } from "../animated-section"

const FAQ_KEYS = [
  "howItWorks",
  "extraCost",
  "partnerShops",
  "chooseOrganisation",
  "transparency",
  "amazon",
  "iosApp",
  "dataPrivacy",
] as const

function useFaqData() {
  const t = useTranslations("faq")
  const { data: projectsData } = useProjects({ size: 20, page: 1 })
  const { data: shopsCountData } = useShopsWithAssociations({ size: 1, page: 1, monetized: true })
  const totalProjects = projectsData?.totalElements ?? 0
  const totalShops = formatNumber(shopsCountData?.totalElements ?? 0)
  return { t, totalProjects, totalShops }
}

function Header() {
  const t = useTranslations("faq")
  return (
    <AnimatedSection>
      <div className="mb-12 text-center">
        <h2 className="mb-4 text-3xl font-bold md:text-4xl">{t("section.title")}</h2>
        <p className="text-xl text-muted-foreground">{t("section.subtitle")}</p>
      </div>
    </AnimatedSection>
  )
}

/** glass — frosted accordion. */
export function FaqGlass() {
  const { t, totalProjects, totalShops } = useFaqData()
  return (
    <section className="py-16">
      <div className="container mx-auto px-4">
        <div className="mx-auto max-w-4xl">
          <Header />
          <Accordion type="single" collapsible className="space-y-4">
            {FAQ_KEYS.map((key, i) => (
              <AnimatedSection key={key} delay={i * 0.05}>
                <AccordionItem
                  value={key}
                  className="rounded-2xl border border-white/15 bg-background/50 px-6 backdrop-blur-md"
                >
                  <AccordionTrigger className="py-6 text-left hover:no-underline [&>svg]:size-5 [&>svg]:stroke-[2.5] [&>svg]:text-foreground">
                    <span className="text-lg font-semibold">{t(`items.${key}.question`)}</span>
                  </AccordionTrigger>
                  <AccordionContent className="pb-6 leading-relaxed text-muted-foreground">
                    {t(`items.${key}.answer`, { totalProjects, totalShops })}
                  </AccordionContent>
                </AccordionItem>
              </AnimatedSection>
            ))}
          </Accordion>
        </div>
      </div>
    </section>
  )
}

